import { useState } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { useNavigate, useSearchParams } from 'react-router';
import { KeyRound, ShieldAlert } from 'lucide-react';
import { resolvePostLoginPath } from '@/app/navigation';
import { BrandMark } from '@/layout/BrandMark';
import { Button } from '@/components/ui/Button';
import { MeDoc } from '@/features/session/meDocument';
import { isWebAuthnSupported, startLogin } from './webauthn';

/**
 * Passkey-only login screen. Clicking the sign-in button starts a WebAuthn
 * assertion ceremony; on success the Go backend returns JWT tokens and we
 * return to the page the manager was on, or the first route they can open.
 * There is no username field — the browser offers any passkey bound to this site.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const client = useApolloClient();
  const [supported] = useState<boolean>(() => isWebAuthnSupported());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSignIn = async () => {
    setBusy(true);
    setError(null);
    try {
      await startLogin();
      let permissions: string[] | null = null;
      let defaultPage: string | null = null;
      try {
        const result = await client.query({ query: MeDoc, fetchPolicy: 'network-only' });
        permissions = result.data?.me?.permissions ?? null;
        defaultPage = result.data?.me?.defaultPage ?? null;
      } catch {
        // Tokens are stored. A missing Me still follows a safe redirect, or /profile.
      }
      navigate(
        resolvePostLoginPath({
          redirect: searchParams.get('redirect'),
          defaultPage,
          permissions,
        }),
        { replace: true },
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-10 dark:bg-gray-900">
      <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 shadow-card dark:border-gray-800 dark:bg-gray-950 dark:shadow-card-dark">
        <div className="mb-8 flex items-center justify-center">
          <BrandMark showWordmark />
        </div>

        <h1 id="admin.login.heading" className="text-center text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
          Sign in with your passkey
        </h1>
        <p className="mt-2 text-center text-sm text-gray-500 dark:text-gray-400">
          Use the passkey your administrator registered for this device.
        </p>

        <div className="mt-8 flex flex-col items-center gap-4 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center dark:border-gray-700 dark:bg-gray-900">
          <KeyRound className="h-12 w-12 text-brand-500" aria-hidden="true" />
          <Button
            id="admin.login.submit"
            onClick={handleSignIn}
            loading={busy}
            disabled={!supported}
            size="lg"
            fullWidth
          >
            {busy ? 'Waiting for passkey…' : 'Use a passkey'}
          </Button>
          {!supported && (
            <p className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-300">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              This browser does not support passkeys. Please use Safari,
              Chrome, Edge or Firefox on a device with biometrics.
            </p>
          )}
          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-gray-500 dark:text-gray-400">
          No passkey yet? Ask a super-administrator for a registration link.
        </p>
      </div>
    </div>
  );
}
